/** IndexedDB persistence for Ticket to Talk (local-first, multi-class). */

import type {
  AttemptResult,
  ClassKey,
  LocalProgress,
} from "./types.js";
import { CLASS_STORAGE_KEY, parseClassKey } from "./types.js";

const DB_NAME = "gota-ticket-to-talk";
/** v2: attempts carry element/licenseClass; profile may store preferredClass */
const DB_VERSION = 2;
const STORE_PROFILE = "profile";
const STORE_ATTEMPTS = "attempts";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_PROFILE)) {
        db.createObjectStore(STORE_PROFILE);
      }
      if (!db.objectStoreNames.contains(STORE_ATTEMPTS)) {
        db.createObjectStore(STORE_ATTEMPTS, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function ensureProfile(
  poolVersion: string,
  preferredClass?: ClassKey,
): Promise<LocalProgress["profile"]> {
  const db = await openDb();
  const tx = db.transaction(STORE_PROFILE, "readwrite");
  const store = tx.objectStore(STORE_PROFILE);
  const existing = await new Promise<LocalProgress["profile"] | undefined>((resolve, reject) => {
    const r = store.get("main");
    r.onsuccess = () => resolve(r.result as LocalProgress["profile"] | undefined);
    r.onerror = () => reject(r.error);
  });
  if (existing) {
    let changed = false;
    if (preferredClass && existing.preferredClass !== preferredClass) {
      existing.preferredClass = preferredClass;
      changed = true;
    }
    if (existing.activePoolVersion !== poolVersion) {
      existing.activePoolVersion = poolVersion;
      changed = true;
    }
    if (changed) store.put(existing, "main");
    await txDone(tx);
    db.close();
    return existing;
  }
  const profile: LocalProgress["profile"] = {
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles",
    createdAt: new Date().toISOString(),
    activePoolVersion: poolVersion,
    preferredClass,
  };
  store.put(profile, "main");
  await txDone(tx);
  db.close();
  return profile;
}

export async function setPreferredClass(classKey: ClassKey): Promise<void> {
  try {
    localStorage.setItem(CLASS_STORAGE_KEY, classKey);
  } catch {
    /* private mode */
  }
  const db = await openDb();
  const tx = db.transaction(STORE_PROFILE, "readwrite");
  const store = tx.objectStore(STORE_PROFILE);
  const existing = await new Promise<LocalProgress["profile"] | undefined>((resolve, reject) => {
    const r = store.get("main");
    r.onsuccess = () => resolve(r.result as LocalProgress["profile"] | undefined);
    r.onerror = () => reject(r.error);
  });
  if (existing) {
    existing.preferredClass = classKey;
    store.put(existing, "main");
  } else {
    store.put(
      {
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles",
        createdAt: new Date().toISOString(),
        activePoolVersion: "",
        preferredClass: classKey,
      },
      "main",
    );
  }
  await txDone(tx);
  db.close();
}

export function readStoredClass(): ClassKey | null {
  try {
    return parseClassKey(localStorage.getItem(CLASS_STORAGE_KEY));
  } catch {
    return null;
  }
}

export async function saveAttempt(result: AttemptResult): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE_ATTEMPTS, "readwrite");
  tx.objectStore(STORE_ATTEMPTS).add({
    ...result,
    savedAt: new Date().toISOString(),
  });
  await txDone(tx);
  db.close();
}

export async function loadAllAttempts(): Promise<AttemptResult[]> {
  const db = await openDb();
  const tx = db.transaction(STORE_ATTEMPTS, "readonly");
  const rows = await new Promise<AttemptResult[]>((resolve, reject) => {
    const r = tx.objectStore(STORE_ATTEMPTS).getAll();
    r.onsuccess = () => resolve((r.result as AttemptResult[]) ?? []);
    r.onerror = () => reject(r.error);
  });
  await txDone(tx);
  db.close();
  return rows;
}

/** Attempts for one pool/element — classes do not bleed. */
export async function loadAttemptsForPool(
  poolVersion: string,
  element: 2 | 3 | 4,
): Promise<AttemptResult[]> {
  const all = await loadAllAttempts();
  return all.filter((a) => {
    if (a.poolVersion !== poolVersion) return false;
    // Legacy rows (pre-v2) lack element — treat as Tech element 2 only when versions match
    if (a.element == null) return element === 2;
    return a.element === element;
  });
}

export type HomeStats = {
  bestMockScore: number | null;
  bestMockTotal: number | null;
  sessionCount: number;
  /** Best consecutive correct across stored attempts for this pool (approx). */
  bestStreak: number;
};

export async function computeHomeStats(
  poolVersion: string,
  element: 2 | 3 | 4,
): Promise<HomeStats> {
  const attempts = await loadAttemptsForPool(poolVersion, element);
  let bestMockScore: number | null = null;
  let bestMockTotal: number | null = null;
  let bestStreak = 0;
  for (const a of attempts) {
    if (a.mode === "full_mock") {
      if (bestMockScore === null || a.score > bestMockScore) {
        bestMockScore = a.score;
        bestMockTotal = a.total;
      }
    }
    let run = 0;
    for (const item of a.items) {
      if (item.isCorrect) {
        run += 1;
        if (run > bestStreak) bestStreak = run;
      } else {
        run = 0;
      }
    }
  }
  return {
    bestMockScore,
    bestMockTotal,
    sessionCount: attempts.length,
    bestStreak,
  };
}

export async function exportLocalProgress(poolVersion: string): Promise<LocalProgress> {
  const profile = await ensureProfile(poolVersion);
  const attempts = await loadAllAttempts();
  return { profile, attempts };
}

export function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
